import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { FormSelectFieldDS } from 'components/reusable/form/FormFields'
import { Button } from 'slab'
import { useGetAdminCompliancePolicies, useGetAdminEmployersEmployerIdComplianceChecks, useGetAdminEmployersEmployerIdComplianceOwners, usePostAdminComplianceChecks } from 'utils/web_api'
import { type AdminComplianceCheck, AdminComplianceCheckEntityType, AdminComplianceCheckPolicyType, AdminComplianceCheckProvider, type AdminComplianceOwner, type AdminCompliancePolicy, type AdminEmployerProfile } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import ComplianceCheckConfirmation from './ComplianceCheckConfirmation'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet'

interface StartCheckFormData {
  entityType: AdminComplianceCheckEntityType
  selectedOwnerId?: string
}

interface EmployerComplianceCheckSheetProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  employerId: string
  employerProfile: AdminEmployerProfile
}

const EmployerComplianceCheckSheet = ({
  isOpen,
  setIsOpen,
  employerId,
  employerProfile,
}: EmployerComplianceCheckSheetProps) => {
  const { reload } = useWindowLocation()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    trigger,
  } = useForm<StartCheckFormData>({
    defaultValues: {
      entityType: undefined,
      selectedOwnerId: undefined,
    }
  })

  const selectedEntityType = watch('entityType')
  const selectedOwnerId = watch('selectedOwnerId')

  const entityTypeDrowndownOptions = [
    { label: 'Employer', value: AdminComplianceCheckEntityType.Employer },
    { label: 'Owner', value: AdminComplianceCheckEntityType['Compliance::Owner'] },
  ]

  // Fetch compliance policies when entity type is selected
  const { data: compliancePolicy, error: policiesError } = useGetAdminCompliancePolicies<AdminCompliancePolicy>(
    {
      provider: AdminComplianceCheckProvider.Footprint,
      entity_type: selectedEntityType,
    },
    {
      query: {
        enabled: !!selectedEntityType,
        retry: false, // Disable automatic retries
      },
    }
  )

  const { data: complianceChecks, error: checksError } = useGetAdminEmployersEmployerIdComplianceChecks<AdminComplianceCheck>(
    employerId,
    {
      query: {
        enabled: selectedEntityType === AdminComplianceCheckEntityType['Compliance::Owner'],
        retry: false,
      },
    }
  )

  // Check if there's a compliance check for the Employer entity
  const hasKYBCheck = Array.isArray(complianceChecks)
    ? complianceChecks.some(
      check => check.entity_type === AdminComplianceCheckEntityType.Employer
    )
    : false

  // Fetch compliance owners when Owner is selected and employer has successful KYB check
  const { data: complianceOwners, error: ownersError } = useGetAdminEmployersEmployerIdComplianceOwners<AdminComplianceOwner>(
    employerId,
    {
      query: {
        enabled: selectedEntityType === AdminComplianceCheckEntityType['Compliance::Owner'] && hasKYBCheck,
        retry: false,
      },
    }
  )

  const selectedOwner = Array.isArray(complianceOwners)
    ? complianceOwners.find((owner: AdminComplianceOwner) => owner.id === selectedOwnerId)
    : undefined

  // Trigger validation when policiesError changes
  useEffect(() => {
    if (selectedEntityType) {
      trigger('entityType')
    }
  }, [policiesError, selectedEntityType, trigger])

  const postComplianceCheck = usePostAdminComplianceChecks({
    mutation: {
      onSuccess: () => {
        toast.success('Compliance check started successfully')
        setIsOpen(false)
        reset()
        reload()
      },
      onError: (error: any) => {
        console.error('Error starting compliance check:', error)
        toast.error('Error starting compliance check: ' + error.message)
      },
    },
  })

  const onSubmit = (data: StartCheckFormData) => {
    const entityId = data.entityType === AdminComplianceCheckEntityType['Compliance::Owner'] ? data.selectedOwnerId! : employerId

    postComplianceCheck.mutate({
      data: {
        entity_id: entityId,
        entity_type: data.entityType,
        provider: AdminComplianceCheckProvider.Footprint,
        policy_type: data.entityType === AdminComplianceCheckEntityType.Employer ? AdminComplianceCheckPolicyType.KYB : AdminComplianceCheckPolicyType.KYC,
      },
    })
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className='h-dvh overflow-y-auto' style={{ minWidth: '500px' }} data-sheet='start-check'>
        <SheetHeader>
          <SheetTitle className='heading-lg'>
            Start a Check
          </SheetTitle>
        </SheetHeader>
        <SheetDescription className='mb-4'>
          Enter details about the check you want to run below.
        </SheetDescription>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <FormSelectFieldDS
            fieldId='entityType'
            fieldLabel='Who is the check being run on?'
            placeholder='Select Entity Type'
            fieldOptions={entityTypeDrowndownOptions}
            register={register}
            validationRule={{
              required: 'Entity type is required',
              validate: () => {
                if (policiesError) {
                  return 'Error loading Policy information. Please try again.'
                } else if (selectedEntityType === AdminComplianceCheckEntityType['Compliance::Owner'] && !hasKYBCheck) {
                  return 'No Check found for corresponding Employer. Before creating Check for Owner, you must first create one for Employer.'
                } else if (checksError) {
                  return 'Error loading Check information. Please try again.'
                } else if (selectedEntityType === AdminComplianceCheckEntityType['Compliance::Owner'] && !complianceOwners) {
                  return 'No Owners found. Please create an Owner first.'
                } else if (selectedEntityType === AdminComplianceCheckEntityType.Employer && ownersError) {
                  return 'Error loading Owner information. Please try again.'
                }

                return undefined
              }
            }}
            error={errors.entityType}
          />

          {/* Owner selection dropdown */}
          {selectedEntityType === AdminComplianceCheckEntityType['Compliance::Owner'] && (
            <FormSelectFieldDS
              fieldId='selectedOwnerId'
              fieldLabel='Which Owner is being checked?'
              placeholder='Select Owner'
              fieldOptions={
              Array.isArray(complianceOwners)
                ? complianceOwners.map(owner => ({
                  label: `${owner.first_name} ${owner.last_name} (${owner.percent_ownership}%)`,
                  value: owner.id,
                }))
                : []
            }
              register={register}
              validationRule={{ required: 'Owner selection is required' }}
              error={errors.selectedOwnerId}
            />
          )}

          {/* Compliance check form content */}
          {isValid && (
            <ComplianceCheckConfirmation
              entityId={selectedEntityType === AdminComplianceCheckEntityType.Employer ? employerId : selectedOwnerId!}
              entityName={selectedEntityType === AdminComplianceCheckEntityType.Employer ? employerProfile.name! : selectedOwner?.first_name + ' ' + selectedOwner?.last_name}
              entityType={selectedEntityType}
              selectedPolicy={compliancePolicy!}
            />
          )}

          <div className='flex justify-end gap-3 pt-4'>
            <Button
              variant='secondary'
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>

            <Button
              data-testid='start-check-button'
              type='submit'
              variant='primary'
              disabled={!isValid || postComplianceCheck.isPending}
            >
              {postComplianceCheck.isPending ? 'Starting...' : 'Start Check'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default EmployerComplianceCheckSheet
